import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, Loader2, Paperclip, RotateCcw, Send, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { base44 } from "@/api/base44Client";
import { clientStorage } from "@/lib/clientStorage";
import { generateVLAPdf } from "@/lib/vlaPdfGeneratorV2";
import { createTelephoneDeclarationPdf } from "@/lib/telephoneDeclarationDocument";
import { createGuidelineCertificationPdf } from "@/lib/guidelineCertificationPdf";
import { GUIDELINES } from "@/components/vla/guidelineCriteria";
import {
  applicationFingerprint,
  guidelineCertificationFromApplication,
  fetchCertificationStatus,
  lodgeCertification,
  lodgingState,
  submitCertification,
} from "@/lib/certification";

const FIRM_NAMES = {
  eas: "EAS Legal",
  vals: "VALS",
  lacw: "Law and Advocacy Centre for Women",
  jbl: "Jarrod Behan Law",
  rga: "RGA",
  admin: "Complete Law Support",
};

// Documents already uploaded on the application that go to the certifier with it.
const UPLOADED = [
  ["centrelink_card_attachment", "centrelink_card_attachment_name", "Client's Centrelink card"],
  ["partner_centrelink_card_attachment", "partner_centrelink_card_attachment_name", "Partner's Centrelink card"],
  ["fees_proof_attachment", "fees_proof_attachment_name", "Proof of payment of legal fees"],
  ["priors_attachment", "priors_attachment_name", "Prior convictions record"],
];

const STATE_STYLES = {
  certified: { box: "bg-green-50 border-green-200 text-green-900", Icon: CheckCircle2 },
  pending: { box: "bg-amber-50 border-amber-200 text-amber-900", Icon: Clock },
  returned: { box: "bg-red-50 border-red-200 text-red-900", Icon: RotateCcw },
  changed: { box: "bg-amber-50 border-amber-200 text-amber-900", Icon: AlertTriangle },
  not_sent: { box: "bg-blue-50 border-blue-200 text-blue-900", Icon: ShieldCheck },
  error: { box: "bg-red-50 border-red-200 text-red-900", Icon: AlertTriangle },
  checking: { box: "bg-slate-50 border-slate-200 text-slate-600", Icon: Loader2 },
};

// 2026-10-16 -> 16/10/2026, as lawyers write it.
const auDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || "");
};
const safeName = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "client";
const pdfFile = (bytes, name) => new File([bytes], name, { type: "application/pdf" });

async function dataUrlToFile(dataUrl, name) {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name, { type: blob.type || "application/octet-stream" });
}

async function upload(file) {
  const { file_url } = await base44.integrations.Core.UploadFile({ file });
  return file_url;
}

/**
 * Shows whether this application has been certified, and sends it for certification.
 * Tells the page whether it may be lodged through `onLodgingChange`.
 */
export default function CertificationPanel({ formData, applicationId, ensureApplicationId, brandType, onLodgingChange }) {
  const [fingerprint, setFingerprint] = useState("");
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [lodgeOpen, setLodgeOpen] = useState(false);
  const checkSeq = useRef(0);

  // Fingerprint the answers, a moment after the last edit.
  useEffect(() => {
    const timer = setTimeout(() => {
      applicationFingerprint(formData).then(setFingerprint).catch(() => setFingerprint(""));
    }, 400);
    return () => clearTimeout(timer);
  }, [formData]);

  const check = useCallback(async () => {
    if (!fingerprint) return;
    const seq = ++checkSeq.current;
    const result = await fetchCertificationStatus({ brand: brandType, applicationId, fingerprint });
    if (seq === checkSeq.current) setStatus(result);
  }, [brandType, applicationId, fingerprint]);

  useEffect(() => { check(); }, [check]);
  // A certifier may decide while the lawyer has the form open.
  useEffect(() => {
    window.addEventListener("focus", check);
    return () => window.removeEventListener("focus", check);
  }, [check]);

  const lodging = useMemo(() => lodgingState(status, fingerprint), [status, fingerprint]);
  useEffect(() => { onLodgingChange?.(lodging); }, [lodging, onLodgingChange]);

  if (lodging.state === "not_required") return null;
  const { box, Icon } = STATE_STYLES[lodging.state] || STATE_STYLES.not_sent;
  const canSend = ["not_sent", "returned", "changed"].includes(lodging.state);

  return (
    <>
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm ${box}`} id="certification-status">
        <span className="flex items-center gap-2">
          <Icon className={`w-4 h-4 shrink-0 ${lodging.state === "checking" ? "animate-spin" : ""}`} />
          {lodging.message}
        </span>
        <span className="flex gap-2">
          {lodging.state === "error" && (
            <Button size="sm" variant="outline" className="bg-white" onClick={check}>Try again</Button>
          )}
          {lodging.state === "pending" && (
            <Button size="sm" variant="outline" className="bg-white" onClick={() => setOpen(true)}>Resend</Button>
          )}
          {canSend && (
            <Button size="sm" className="gap-1.5 bg-[#0071BC] hover:bg-[#005A96]" onClick={() => setOpen(true)}>
              <ShieldCheck className="w-4 h-4" /> Send for certification
            </Button>
          )}
          {lodging.state === "certified" && !lodging.sentToLodge && (
            <Button size="sm" className="gap-1.5 bg-green-700 hover:bg-green-800" onClick={() => setLodgeOpen(true)}>
              <Send className="w-4 h-4" /> Send to Complete Law Support to lodge
            </Button>
          )}
        </span>
      </div>
      {lodgeOpen && status?.certification && (
        <LodgeDialog
          formData={formData}
          certification={status.certification}
          onClose={() => setLodgeOpen(false)}
          onSent={() => { setLodgeOpen(false); check(); }}
        />
      )}
      {open && (
        <SendDialog
          formData={formData}
          brandType={brandType}
          certifiers={status?.certifiers || []}
          ensureApplicationId={ensureApplicationId}
          onClose={() => setOpen(false)}
          onSent={() => { setOpen(false); check(); }}
        />
      )}
    </>
  );
}

function SendDialog({ formData, brandType, certifiers, ensureApplicationId, onClose, onSent }) {
  const clientName = [formData.first_name, formData.last_name].filter(Boolean).join(" ");
  const brandKey = brandType ? `${brandType}_` : "";

  // Guideline certifications completed on this computer, best match for this client first.
  const guidelines = useMemo(() => {
    let list = [];
    try { list = JSON.parse(clientStorage.getItem(`${brandKey}guidelines_completed_list`) || "[]"); } catch { list = []; }
    const wanted = clientName.trim().toLowerCase();
    return (Array.isArray(list) ? list : [])
      .filter((g) => g && g.form_data)
      .sort((a, b) => {
        const am = String(a.client_name || "").trim().toLowerCase() === wanted ? 1 : 0;
        const bm = String(b.client_name || "").trim().toLowerCase() === wanted ? 1 : 0;
        return bm - am || String(b.completed_at || "").localeCompare(String(a.completed_at || ""));
      });
  }, [brandKey, clientName]);

  const [guidelineId, setGuidelineId] = useState(() => {
    const first = guidelines[0];
    return first && String(first.client_name || "").trim().toLowerCase() === clientName.trim().toLowerCase() ? first.id : "";
  });
  const [extra, setExtra] = useState([]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("");
  const fileInput = useRef(null);

  const isTelephone = formData.declaration_method === "telephone_declaration";
  const uploaded = UPLOADED.filter(([field]) => formData[field]);
  // The guideline certification comes from question 21 of this application. The separate
  // Guideline Certification form is only asked for when question 21 hasn't been done.
  const inApplication = Boolean(GUIDELINES[formData.vla_guideline_code]);
  const tickedCriteria = Object.values(formData.guideline_criteria || {}).filter(Boolean).length;
  const guideline = inApplication
    ? { guideline: formData.vla_guideline_code, form_data: guidelineCertificationFromApplication(formData) }
    : guidelines.find((g) => g.id === guidelineId);
  const guidelineReady = inApplication ? tickedCriteria > 0 || Boolean(formData.guideline_waiver_applies) : Boolean(guideline);
  const recipients = certifiers.map((c) => c.name || c.email).join(" and ");

  const send = async () => {
    if (!guidelineReady) {
      toast.error(inApplication ? "Tick the guideline criteria at question 21 first." : "Choose the guideline certification for this client.");
      return;
    }
    setSending(true);
    try {
      const applicationId = ensureApplicationId();
      // Fingerprint the answers before anything is built from them, so what is certified
      // is exactly what the application shows - not whatever building a document left behind.
      const fingerprint = await applicationFingerprint(formData);
      const who = safeName(clientName);
      const documents = [];
      const add = async (label, file) => documents.push({ label, name: file.name, url: await upload(file) });

      setStep("Preparing the application…");
      await add("Legal aid application", pdfFile(await generateVLAPdf(formData), `VLA_Application_${who}.pdf`));

      if (isTelephone) {
        setStep("Preparing the telephone declaration…");
        await add("Telephone declaration", pdfFile(await createTelephoneDeclarationPdf(formData, brandType), `Telephone_Declaration_${who}.pdf`));
      }

      setStep("Preparing the guideline certification…");
      const guidelinePdf = createGuidelineCertificationPdf(guideline.form_data, { appName: FIRM_NAMES[brandType] || "Legal aid" });
      await add("Guideline certification", pdfFile(guidelinePdf.output("arraybuffer"), `Guideline_Certification_${who}.pdf`));

      setStep("Adding the supporting documents…");
      for (const [field, nameField, label] of uploaded) {
        const url = String(formData[field]);
        const name = formData[nameField] || `${safeName(label)}.pdf`;
        // Files uploaded before storage existed are held inline; store them properly first.
        if (url.startsWith("data:")) await add(label, await dataUrlToFile(url, name));
        else documents.push({ label, name, url });
      }
      for (const file of extra) await add(file.name, file);

      setStep("Sending…");
      const result = await submitCertification({
        brand: brandType,
        application_id: applicationId,
        data_hash: fingerprint,
        client_name: clientName,
        file_number: formData.file_number || formData.file_no || formData.declaration_file_reference || "",
        guideline: GUIDELINES[guideline.form_data.guideline]?.label || guideline.guideline || "",
        documents,
        note,
      });
      if (result.emailed === false) {
        toast.warning(`Sent for certification, but the email couldn't be sent (${result.emailError}). The certifiers can still see it in the portal.`);
      } else {
        toast.success(`Sent to ${recipients || "the certifiers"} for certification.`);
      }
      onSent();
    } catch (error) {
      toast.error(error?.message || "The application couldn't be sent for certification.");
    } finally {
      setSending(false);
      setStep("");
    }
  };

  return (
    <Dialog open onOpenChange={(value) => { if (!value && !sending) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#0071BC]" /> Send for certification</DialogTitle>
          <DialogDescription>
            {recipients ? `${recipients} will be emailed these documents.` : "The firm's certifiers will be emailed these documents."} It can be lodged once one of them certifies it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <ul className="space-y-1.5">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Legal aid application</li>
            {isTelephone && <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Telephone declaration</li>}
            {uploaded.map(([field, nameField, label]) => (
              <li key={field} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> {label} <span className="text-slate-400 truncate">({formData[nameField] || "attached"})</span></li>
            ))}
          </ul>

          <div className="space-y-1.5">
            <label className="font-medium text-slate-700" htmlFor="cert-guideline">Guideline certification</label>
            {inApplication ? (
              guidelineReady ? (
                <p className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  <span>
                    {GUIDELINES[formData.vla_guideline_code].label}
                    <span className="text-slate-400"> – from question 21, {tickedCriteria} {tickedCriteria === 1 ? "criterion" : "criteria"} ticked{formData.guideline_waiver_applies ? ", means test waiver" : ""}</span>
                  </span>
                </p>
              ) : (
                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                  No guideline criteria are ticked yet. Go to question 21 and tick the criteria this application meets.
                </p>
              )
            ) : guidelines.length ? (
              <select
                id="cert-guideline"
                value={guidelineId}
                onChange={(e) => setGuidelineId(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3"
              >
                <option value="">Choose…</option>
                {guidelines.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.client_name} – {GUIDELINES[g.form_data.guideline]?.label || g.guideline}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                Choose the VLA guideline at question 21 and tick its criteria, then send for certification.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="font-medium text-slate-700">Other documents</p>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="application/pdf,image/*,.doc,.docx"
              className="hidden"
              onChange={(e) => { setExtra((prev) => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ""; }}
            />
            {extra.map((file, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-slate-200 px-2 py-1">
                <span className="truncate">{file.name}</span>
                <button type="button" onClick={() => setExtra((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove ${file.name}`}>
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => fileInput.current?.click()} disabled={sending}>
              <Paperclip className="w-4 h-4" /> Add documents
            </Button>
          </div>

          <div className="space-y-1.5">
            <label className="font-medium text-slate-700" htmlFor="cert-note">Note for the certifier (optional)</label>
            <Textarea id="cert-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {step && <span className="text-xs text-slate-500 self-center mr-auto">{step}</span>}
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending || !guidelineReady} className="gap-1.5 bg-[#0071BC] hover:bg-[#005A96]">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Send for certification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Once certified, the application goes to Complete Law Support to lodge from here. It is the
// Apply for Aid worksheet for a new grant with everything the application already knows
// filled in, and it sends the documents the certifier certified - nothing is re-attached.
function LodgeDialog({ formData, certification, onClose, onSent }) {
  const [urgent, setUrgent] = useState(false);
  const [urgencyDetails, setUrgencyDetails] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  const charges = Array.isArray(formData.criminal_charges) ? formData.criminal_charges : [];
  const summary = {
    custody_status: formData.currently_in_custody === "Yes"
      ? `In custody${formData.custody_facility ? ` - ${formData.custody_facility}` : ""}`
      : formData.currently_in_custody === "No" ? "Not in custody" : "",
    court_location: formData.court_tribunal || "",
    next_court_date: auDate(formData.hearing_date),
    matter_type: formData.law_type || "",
    charges,
  };
  const rows = [
    ["Client", certification.client_name],
    ["File reference", certification.file_number],
    ["Guideline", certification.guideline],
    ["Custody", summary.custody_status],
    ["Court", summary.court_location],
    ["Next court date", summary.next_court_date],
    ["Charges", charges.join("; ")],
    ["Certified by", certification.decided_by],
  ].filter(([, value]) => value);

  const send = async () => {
    if (urgent && !urgencyDetails.trim()) {
      toast.error("Say why it's urgent.");
      return;
    }
    setSending(true);
    try {
      const result = await lodgeCertification(certification.id, { urgent, urgency_details: urgencyDetails, notes, summary });
      if (result.emailed === false) toast.warning("Sent to Complete Law Support's queue. The email copy couldn't be sent.");
      else toast.success("Sent to Complete Law Support to lodge.");
      onSent();
    } catch (error) {
      toast.error(error?.message || "It couldn't be sent. Please try again.");
      // Someone else may have sent it in the meantime; refresh what the bar shows.
      if (error?.status === 409) onSent();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(value) => { if (!value && !sending) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-green-700" /> Send to Complete Law Support to lodge</DialogTitle>
          <DialogDescription>
            This sends the certified application and its documents to be lodged with VLA. There's nothing to attach or re-enter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <dl className="rounded-md border border-slate-200 divide-y divide-slate-100">
            {rows.map(([label, value]) => (
              <div key={label} className="flex gap-3 px-3 py-1.5">
                <dt className="w-32 shrink-0 text-slate-500">{label}</dt>
                <dd className="text-slate-800">{value}</dd>
              </div>
            ))}
          </dl>

          <div>
            <p className="font-medium text-slate-700 mb-1">Documents (as certified)</p>
            <ul className="space-y-1">
              {(certification.documents || []).map((doc, i) => (
                <li key={i} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> {doc.label || doc.name}</li>
              ))}
            </ul>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} className="w-4 h-4 accent-red-600" />
            <span className="font-medium text-slate-700">This is urgent</span>
          </label>
          {urgent && (
            <Textarea value={urgencyDetails} onChange={(e) => setUrgencyDetails(e.target.value)} rows={2} placeholder="Why is it urgent? e.g. in custody, hearing on Friday" autoFocus />
          )}

          <div className="space-y-1.5">
            <label className="font-medium text-slate-700" htmlFor="lodge-notes">Notes for Complete Law Support (optional)</label>
            <Textarea id="lodge-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="gap-1.5 bg-green-700 hover:bg-green-800">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to lodge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
